import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';

const ApexComparison = () => {
  const navigate = useNavigate();

  const goToApplication = () => {
    navigate('/5k-guarantee#application');
  };

  const genericAgency = [
    { id: 'generic-1', text: 'Cookie-cutter marketing templates' },
    { id: 'generic-2', text: 'Work with any industry' },
    { id: 'generic-3', text: 'Focus on getting more leads' },
    { id: 'generic-4', text: 'Monthly reports you never read' },
    { id: 'generic-5', text: 'Account manager who does not know fitness' },
    { id: 'generic-6', text: 'Compete with other local gyms they serve' }
  ];

  const apexApproach = [
    { id: 'apex-1', text: 'Tailored 1-2-1 coaching for YOUR business' },
    { id: 'apex-2', text: 'Fitness sales specialists — it\'s all we do' },
    { id: 'apex-3', text: 'Convert more of the leads you already have' },
    { id: 'apex-4', text: 'Hands-on audits and real-time improvements' },
    { id: 'apex-5', text: '7,500+ consultations in boutique gyms' },
    { id: 'apex-6', text: 'Only ONE partner per local area' }
  ];

  return (
    <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0d1f12]/30 to-[#0A0A0A]" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            The Apex Difference
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
            Not Another Marketing Agency
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            Just like you give tailored training programmes to your clients, 
            we give tailored sales coaching to your business. Generic does not work.
          </p>
        </div>

        {/* Comparison cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Generic Agency Card */}
          <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 relative overflow-hidden">
            {/* Strikethrough decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Generic Marketing Agency</h3>
              </div>
              
              <ul className="space-y-4">
                {genericAgency.map((item) => (
                  <li key={item.id} data-testid={item.id} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[#A1A1AA]">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Apex Approach Card */}
          <div className="bg-gradient-to-br from-[#121212] to-[#0d1f12] border border-[#22C55E]/30 rounded-2xl p-8 relative overflow-hidden">
            {/* Glow effect */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#22C55E]/10 rounded-full blur-[80px]" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                </div>
                <h3 className="text-xl font-bold text-white">Apex Fit Sales Coaching</h3>
              </div>
              
              <ul className="space-y-4">
                {apexApproach.map((item) => (
                  <li key={item.id} data-testid={item.id} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                    <span className="text-white">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={goToApplication}
            data-testid="comparison-cta"
            className="group inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
          >
            See If You Qualify
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-[#71717A] text-sm mt-4">
            Limited to one gym per area — check availability now
          </p>
        </div>
      </div>
    </section>
  );
};

export default ApexComparison;
